import classNames from "classnames";

import { Case, Clinic, ClinicEMRMap, User } from "pwapi";

import useClinicFields, {
  integrationObjectForUpdate,
} from "shared/hooks/useClinicFields";
import { getClinicPhone } from "shared/utils/clinicUtils";
import { getUpdatedBillingType } from "shared/utils/rulesEngineUtils";
import {
  getValueFromObject,
  requiredError,
} from "shared/utils/validationUtils";

import { useAppSelector } from "app/hooks";
import { AccessionSelect, Input } from "ui/accessioning/AccessionForm";
import { UserOption } from "ui/CaseFormInputs/SelectSearchOptions";
import useCaseInputs from "ui/CaseFormInputs/useCaseInputs";

import FormHeaders from "./FormHeaders";

const IntegrationsForm = ({
  onHandlePatchAccession,
  displaySubmit,
  dirty,
  onHandleDiscardChanges,
  onChangeClinic,
  onChangeOrderingDoctor,
  clearValues,
  values,
  forceModMed,
  flatIntegration = false,
  skipMRN = false,
  errors = {},
  touched = {},
}: {
  onHandlePatchAccession?: Function;
  displaySubmit?: boolean;
  dirty?: boolean;
  onHandleDiscardChanges?: Function;
  onChangeClinic?: Function;
  onChangeOrderingDoctor?: Function;
  initialClinic?: Clinic;
  clearValues?: Function;
  // TODO: fix Any
  values: any;
  forceModMed?: boolean;
  flatIntegration?: boolean;
  skipMRN?: boolean;
  errors?: any;
  touched?: any;
}) => {
  const { clinicId, orderingDoctorId, supervisingPhysicianId } =
    useCaseInputs();

  const clinics = useAppSelector((state) => state.clinics);
  const emr = useClinicFields({
    clinic: values.clinic,
    ordering_doctor_id: values.ordering_doctor?.id,
    flatIntegration,
    skipMRN,
  });
  const hasSupervisingPhysician =
    values.clinic && values.clinic.use_supervising_physician;
  const clinicError = getValueFromObject(errors, "clinic");
  const orderingDoctorError = getValueFromObject(errors, "ordering_doctor");
  const phone = getClinicPhone(values.clinic);

  return (
    <>
      <FormHeaders
        title="Clinic and External Order"
        displaySubmit={displaySubmit}
        dirty={dirty}
        errors={errors}
        touched={touched}
        forms={[
          "clinic",
          "external_ezderm_order_id",
          "external_order_id",
          "external_patient_id",
          "ordering_doctor",
          "patient.external_patient_id",
          "supervising_physician",
        ]}
        onHandleDiscardChanges={onHandleDiscardChanges}
        onHandleClearForm={() => {
          onHandlePatchAccession &&
            onHandlePatchAccession({
              clinic_id: null,
              external_order_id: forceModMed ? values.external_order_id : null, // We can send "undefined" here to skip the field
              external_ezderm_order_id: null,
              ordering_doctor_id: null,
              supervising_physician_id: null,
              patient: {
                ...values.patient,
                external_patient_id: forceModMed
                  ? values.patient.external_patient_id // ... but not here because patient is a json blob
                  : null,
              },
              external_accession: undefined,
              // TODO: loop over all integration values to clear them
              // Requires a deep merge
            });
          clearValues && clearValues();
        }}
      />
      <div className="LISForm__section">
        <div
          className={classNames(
            "Accession__input",
            "Accession__input--required",
            hasSupervisingPhysician ? "Input--2" : "Input--3",
            clinicError &&
              !requiredError(clinicError) &&
              getValueFromObject(touched, "clinic") &&
              "Accession__input--error",
            clinicError && "Accession__input--submission-check-error"
          )}
        >
          <label>{clinicId.label} *</label>
          {/* TODO: Follow-up for James would be scope this list to ModMed clinics only,
              ... if we are "forcing" ModMed via the kickstart */}
          <AccessionSelect
            {...clinicId}
            handleOnChange={onChangeClinic}
            filters={{
              orderingDoctor: values.ordering_doctor?.id,
            }}
            handleOnBlur={
              onHandlePatchAccession &&
              ((value: Clinic) => {
                const availableBillingTypes =
                  (value?.id && clinics?.[value.id].billing_types) || [];
                const updatedBillingType = value.id
                  ? getUpdatedBillingType({
                      initialBillingType: values.billing_type,
                      availableBillingTypes: availableBillingTypes,
                    }) || null
                  : values.billing_type;
                // What's the forceModMed ? undefined : null about?
                // We we don't want to clear these fields if we are "forcing"
                // ModMed, i.e. we started a

                const patient = {
                  ...values.patient,
                  external_patient_id: forceModMed
                    ? values.patient.external_patient_id // ... but not here because patient is a json blob
                    : null,
                };

                onHandlePatchAccession(
                  {
                    clinic_id: value?.id,
                    ...(value?.id !== values.clinic?.id && {
                      supervising_physician_id: null,
                    }),
                    external_order_id: forceModMed
                      ? values.external_order_id
                      : null, // We can send "undefined" here to skip the field
                    external_ezderm_order_id: null,
                    patient,
                    // NOTE: this no longer forces external_accession to ""
                    billing_type: updatedBillingType,
                  },
                  !values.billing_type ||
                    updatedBillingType !== values.billing_type
                );
              })
            }
            required
          >
            {values.clinic ? (
              <>
                {values.clinic.name}
                {phone && <> {phone}</>}
              </>
            ) : (
              <>Select a clinic...</>
            )}
          </AccessionSelect>
        </div>
        <div
          className={classNames(
            "Accession__input",
            "Accession__input--required",
            hasSupervisingPhysician ? "Input--2" : "Input--3",
            orderingDoctorError &&
              !requiredError(orderingDoctorError) &&
              getValueFromObject(touched, "ordering_doctor") &&
              "Accession__input--error",
            orderingDoctorError && "Accession__input--submission-check-error"
          )}
        >
          <label>{orderingDoctorId.label} *</label>
          <AccessionSelect
            {...orderingDoctorId}
            filters={{
              clinicId: values?.clinic?.id,
            }}
            handleOnChange={onChangeOrderingDoctor}
            handleOnBlur={
              onHandlePatchAccession &&
              ((value: User) => {
                const ordering_doctor_id = value?.id;
                const skip_provider_ids = values?.clinic?.emr_mappings?.flatMap(
                  (em: ClinicEMRMap) => em?.settings?.skip_provider_ids || []
                );
                const clearIntegrationFields =
                  ordering_doctor_id &&
                  skip_provider_ids?.includes(ordering_doctor_id);

                onHandlePatchAccession({
                  ordering_doctor_id: value?.id,
                  ...(clearIntegrationFields
                    ? {
                        external_order_id: null,
                        external_ezderm_order_id: null,
                        patient: {
                          ...values.patient,
                          external_patient_id: null,
                        },
                      }
                    : {}),
                });
              })
            }
            required
          >
            {values.ordering_doctor ? (
              <UserOption object={values.ordering_doctor} />
            ) : (
              "Select an ordering provider..."
            )}
          </AccessionSelect>
        </div>
        {hasSupervisingPhysician && (
          <div
            className={classNames(
              "Accession__input",
              "Input--2",
              getValueFromObject(errors, "supervising_physician") &&
                getValueFromObject(touched, "supervising_physician") &&
                "Accession__input--error",
              getValueFromObject(errors, "supervising_physician") &&
                "Accession__input--submission-check-error"
            )}
            data-testid="supervising-physician"
          >
            <label>{supervisingPhysicianId.label}</label>
            <AccessionSelect
              {...supervisingPhysicianId}
              filters={{
                clinicId: values?.clinic?.id,
              }}
              handleOnBlur={
                onHandlePatchAccession &&
                ((value: User) => {
                  onHandlePatchAccession({
                    supervising_physician_id: value?.id,
                  });
                })
              }
            >
              {values.supervising_physician ? (
                <UserOption object={values.supervising_physician} />
              ) : (
                "Select a supervising physician..."
              )}
            </AccessionSelect>
          </div>
        )}
        <div className="Accession__input--vertical Accession__input--full">
          {emr?.fields?.map((field) => {
            return (
              <Input
                {...field}
                required={
                  values.case_type === Case.type.CONSULT
                    ? false
                    : field.required
                }
                handleOnBlur={
                  onHandlePatchAccession &&
                  ((value: string) => {
                    // Use function to convert name '.' notation into an object
                    // That is merged over existing values as we cannot specify
                    // individual values in the PATCH as the backend just deals
                    // with entire "jsonb" blobs for Acccessions.
                    const objectPath = integrationObjectForUpdate(
                      field.name,
                      value,
                      values
                    );
                    onHandlePatchAccession(objectPath);
                  })
                }
                key={field.id}
                error={getValueFromObject(errors, field.name)}
                touched={getValueFromObject(touched, field.name)}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

export default IntegrationsForm;
